import { RoleFunction } from '@eagle/common';
import { Group, UserGroup } from '@eagle/core-data-types';
import { Chip, styled, Typography } from '@ethings/ui-toolkit';
import { SxProps } from '@mui/system';
import { ComponentProps, FC } from 'react';
import { Portals } from '../../types';
import { CacheDataTypes } from '../../types/cache';
import { switchToPortal, useHasAuthorization } from '../../util';
import { FetchAll } from '../fetch';
import { useBoolFlag } from '../flags';

interface GroupChipElementProps {
  'data-testid'?: string;
  groupIds: string[];
  isClickable?: boolean;
  noResultsText?: string;
  type?: CacheDataTypes;
  sx?: SxProps;
  path?: string;
}

interface StyledChipProps extends ComponentProps<typeof Chip> {
  sx?: SxProps;
}

const StyledChip = styled(Chip)<StyledChipProps>(({ theme }) => ({
  marginRight: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
}));

const ADMIN_PORTAL_ROLES = [RoleFunction.ADMINISTRATOR] as const;

const GroupChipRender: FC<{ 'data-testid'?: string; groupData: Group | UserGroup; isClickable: boolean; sx?: SxProps; path?: string }> = ({ groupData, isClickable, sx, path = 'group', ...props }) => {
  const { hasAuthorization } = useHasAuthorization();
  const hasAdminPortalPermissions = hasAuthorization(ADMIN_PORTAL_ROLES);
  const groupNavigationFlag = useBoolFlag('portals-group-navigation-component');
  const clickEnabled = isClickable && hasAdminPortalPermissions && groupNavigationFlag;

  return (
    <StyledChip
      data-testid={props['data-testid']}
      key={groupData._id}
      clickable={clickEnabled}
      component={clickEnabled ? 'a' : 'div'}
      {...(clickEnabled && { href: switchToPortal(null, Portals.ADMIN, `${path}/${groupData._id}`) })}
      label={groupData.display}
      size="small"
      variant="outlined"
      sx={sx}
    />
  );
};

export const GroupChipElement: FC<GroupChipElementProps> = ({ groupIds, isClickable = false, noResultsText, sx, path, type = CacheDataTypes.GROUP, ...props }) => {
  if (groupIds.length < 1) {
    return noResultsText ? (
      <Typography variant='body2' fontStyle="italic" {...props}>{noResultsText}</Typography>
    ) : <></>;
  }

  const set = new Set<string>(groupIds);
  return (
    <FetchAll
      dataType={type}
      filterFactory={(groupData: Group) => set.has(groupData._id)}
      renderFactory={(groupData: Group, key) => <GroupChipRender key={key} groupData={groupData} isClickable={isClickable} path={path} sx={sx} data-testid={props['data-testid']} />}
    />
  );
};

